/**
 * VENNER — Gift with purchase
 *
 *  · <venner-gift>       lives on the product page. When the main product is added to the cart and the
 *                        toggle is on, it adds the gift in the same operation.
 *  · <venner-gift-guard> lives in the cart page and the drawer. It removes the gift when it no longer
 *                        applies; Liquid decides which line has to go, this only carries it out.
 *
 * The gift is never added when the toggle is switched on: if it were, someone could end up with the gift
 * alone in the cart and pay full price for it. It's only added after the server confirms that the product
 * that qualifies for it is already in the cart.
 */
if (!window.__vennerGiftReady) {
  window.__vennerGiftReady = true;

  const REMOVED_NOTICE_KEY = 'venner:gift-removed';
  const rootUrl = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /** The sections that ask to be re-rendered (cart, drawer) so the change shows up right away. */
  function bundledSections() {
    const sections = [];

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } })
    );

    return sections.join(',');
  }

  function announceCartChange(cart) {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        // `line-item:change` re-renders the cart without reopening the drawer: Prestige already opened it.
        detail: { baseEvent: 'line-item:change', cart: cart }
      })
    );
  }

  function saveToSession(key, value) {
    try {
      if (value) sessionStorage.setItem(key, value);
      else sessionStorage.removeItem(key);
    } catch (error) {
      // Private browsing: the notice is lost, the cart isn't.
    }
  }

  function takeFromSession(key) {
    try {
      const value = sessionStorage.getItem(key);
      sessionStorage.removeItem(key);
      return value;
    } catch (error) {
      return null;
    }
  }

  // ------------------------------------------------------------------ product page

  class VennerGift extends HTMLElement {
    connectedCallback() {
      this.toggleInput = this.querySelector('.vgift__input');

      if (!this.toggleInput || !this.productForm()) return;

      if (this.dataset.hideDynamicCheckout) {
        document.documentElement.classList.add('venner-gift-hides-dynamic');
      }

      this.onToggleChange = () => this.sync();
      this.onVariantAdd = (event) => this.handleVariantAdd(event);

      this.toggleInput.addEventListener('change', this.onToggleChange);
      document.addEventListener('variant:add', this.onVariantAdd);

      this.sync();
    }

    disconnectedCallback() {
      this.toggleInput?.removeEventListener('change', this.onToggleChange);
      document.removeEventListener('variant:add', this.onVariantAdd);
      document.documentElement.classList.remove('venner-gift-on');
    }

    /**
     * The form is looked up every time, never stored: on a variant change Prestige re-renders that part of
     * the page with server HTML, and a stored reference would point to an element that's no longer on the
     * page.
     */
    productForm() {
      return document.forms[this.dataset.form];
    }

    /**
     * The buy button text is completed from CSS, not by writing into the button: Prestige replaces
     * <buy-buttons> with server HTML on every variant change and would wipe anything we had put there.
     * A class on <html> does survive.
     */
    sync() {
      document.documentElement.classList.toggle('venner-gift-on', this.toggleInput.checked);
    }

    async handleVariantAdd(event) {
      if (event.target !== this.productForm() || !this.toggleInput.checked) return;

      // Two quick clicks on "Add to cart" fire two events, and the second one arrives with a cart that
      // doesn't include the first gift yet: without this flag, two gifts would be added.
      if (this.isAdding) return;

      const cart = event.detail && event.detail.cart;
      const lines = (cart && cart.items) || [];
      const qualifies = lines.some((line) => line.handle === this.dataset.handle);
      const alreadyInCart = lines.some((line) => line.properties && line.properties._gift_for);

      // The server has the final say: the gift goes in only if it confirms the qualifying product did.
      if (!qualifies || alreadyInCart) return;

      this.isAdding = true;

      try {
        await this.addGift();
      } finally {
        this.isAdding = false;
      }
    }

    async addGift() {
      const field = this.querySelector('[data-vgift-variant]');

      if (!field || !field.value) return;

      const formData = new FormData();

      formData.set('id', field.value);
      formData.set('quantity', '1');
      formData.set('properties[_gift_for]', this.dataset.handle || '');

      if (this.dataset.until) formData.set('properties[_gift_until]', this.dataset.until);
      if (this.dataset.note) formData.set('properties[_gift_note]', this.dataset.note);
      if (this.dataset.removedNotice) formData.set('properties[_gift_notice]', this.dataset.removedNotice);

      formData.set('sections', bundledSections());

      try {
        const response = await fetch(`${rootUrl()}cart/add.js`, {
          method: 'POST',
          body: formData,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        if (!response.ok) {
          this.showAddError();
          return;
        }

        const added = await response.json();
        const cart = await (await fetch(`${rootUrl()}cart.js`)).json();

        cart.sections = added.sections;
        announceCartChange(cart);
      } catch (error) {
        this.showAddError();
      }
    }

    /**
     * If the gift didn't make it in, the order is still valid: the only thing missing is the gift. The
     * customer is told on the card instead of blocking anything.
     */
    showAddError() {
      if (!this.dataset.error) return;

      let errorMessage = this.querySelector('.vgift__error');

      if (!errorMessage) {
        errorMessage = document.createElement('p');
        errorMessage.className = 'vgift__error';
        errorMessage.setAttribute('role', 'alert');
        this.querySelector('.vgift__card')?.appendChild(errorMessage);
      }

      errorMessage.textContent = this.dataset.error;
    }
  }

  // ------------------------------------------------------------------ cart page and drawer

  /*
    If the cart rejects the change, the section is re-rendered with the same line and the guard would
    retry forever. What has already been attempted is remembered so it doesn't get stuck in that loop.
  */
  const attemptedKeys = new Set();

  class VennerGiftGuard extends HTMLElement {
    async connectedCallback() {
      const lineKey = this.dataset.lineKey;

      if (!lineKey) {
        this.showRemovedNotice();
        return;
      }

      if (attemptedKeys.has(lineKey)) return;

      attemptedKeys.add(lineKey);

      const quantity = parseInt(this.dataset.quantity, 10) || 0;

      saveToSession(REMOVED_NOTICE_KEY, quantity === 0 ? this.dataset.notice : '');

      try {
        const response = await fetch(`${rootUrl()}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity: quantity, sections: bundledSections() })
        });

        if (!response.ok) return;

        if (window.themeVariables?.settings?.pageType === 'cart') {
          window.location.reload();
          return;
        }

        announceCartChange(await response.json());
      } catch (error) {
        // The cart didn't change: worst case the gift is still there and the next re-render retries.
        attemptedKeys.delete(lineKey);
      }
    }

    /** The notice is shown once the line is gone, when the cart has been re-rendered. */
    showRemovedNotice() {
      const text = takeFromSession(REMOVED_NOTICE_KEY);

      if (!text) return;

      const paragraph = document.createElement('p');

      paragraph.className = 'vgift-guard__notice';
      paragraph.setAttribute('role', 'status');
      paragraph.textContent = text;
      this.appendChild(paragraph);
    }
  }

  if (!window.customElements.get('venner-gift')) {
    window.customElements.define('venner-gift', VennerGift);
  }

  if (!window.customElements.get('venner-gift-guard')) {
    window.customElements.define('venner-gift-guard', VennerGiftGuard);
  }
}
